import { WebhookCreate, WebhookMethod } from '@/api/dashboard'
import { FieldGroup } from '@/ui'
import { useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { dashboardClient } from '@/api'
import { GAME_WEBHOOKS_DETAIL_PATH, urlRegexp, useGenerateCompanyPath } from '@/libs'
import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalProps,
  ModalTitle,
  Textarea,
  formatHookFormErrors,
} from '@dashboard/ui'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { SelectEventsButton } from '@/layouts/webhooks/widgets/SelectEventsButton'
import { webhooksQuery } from '@/layouts/webhooks/api'
import { MAX_DESCRIPTION_LENGTH } from '@/Settings'

export const CreateWebhookModal = (props: ModalProps) => {
  const { ...rest } = props
  const ql = useQueryClient()
  const { companyId, gameId, generatePath } = useGenerateCompanyPath()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const formMethods = useForm<WebhookCreate>({
    defaultValues: {
      method: WebhookMethod.POST,
      url: '',
      description: '',
      events: [],
    },
  })

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setError,
  } = formMethods

  const saveClick = async (data: WebhookCreate) => {
    if (!data.events?.length) {
      setError('events', { message: t('validation.required') })
      return
    }

    const { data: webhook } = await dashboardClient.v1.createWebhook(companyId, gameId, data)
    props.onClose?.()
    navigate(generatePath(GAME_WEBHOOKS_DETAIL_PATH, { webhookId: webhook.id }), { replace: true })
    await ql.invalidateQueries({ queryKey: webhooksQuery(companyId, gameId).queryKey })
  }

  return (
    <Modal {...rest}>
      <form onSubmit={handleSubmit(saveClick)}>
        <ModalContent>
          <ModalTitle>{t('webhook.form.header.title')}</ModalTitle>

          <div
            className="mb-4 text-paragraph-sm text-text-secondary"
            dangerouslySetInnerHTML={{ __html: t('webhook.form.header.sub') }}
          />

          <FieldGroup label={t('webhook.form.endpoint-url')} required={true}>
            <Input
              {...register('url', {
                required: t('validation.required'),
                pattern: { value: urlRegexp, message: t('validation.invalid_url') },
                setValueAs: value => value.trim(),
              })}
              {...formatHookFormErrors(errors, 'url')}
              placeholder="https://example.com"
              autoFocus={true}
            />
          </FieldGroup>

          <FieldGroup label={t('webhook.form.desc')}>
            <Textarea
              {...register('description', {
                maxLength: {
                  message: t('validation.maxLength', {
                    field: t('webhook.form.desc'),
                    value: MAX_DESCRIPTION_LENGTH,
                  }),
                  value: MAX_DESCRIPTION_LENGTH,
                },
              })}
              {...formatHookFormErrors(errors, 'description')}
              placeholder={t('webhook.form.description-placeholder')}
              rows={6}
            />
          </FieldGroup>
          <FormProvider {...formMethods}>
            <SelectEventsButton />
          </FormProvider>
        </ModalContent>

        <ModalFooter>
          <Button size="sm" onClick={props.onClose} color="secondary" variant="outline" type="button">
            {t('Cancel')}
          </Button>
          <Button size="sm" color="primary" type="submit" loading={isSubmitting}>
            {t('Add')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
